// @flow

import {flattenDeep} from 'lodash';
import type {
  MarkdocNodeRegistration,
  Config as MarkdocConfig,
} from 'docs/markdoc';
import {Tag, isMarkdocAstTag, findMarkdocTag} from 'docs/util';

import type {
  RenderNode as MarkdocRenderNode,
  Node as MarkdocAstNode,
} from '@markdoc/markdoc';
import type {CodeBlockHighlights} from './CodeBlock';

// TODO: Fix the type of MarkdocRenderNode to account for children that are arrays of Nodes (not just tuples)
// Then we can fix this type
type IntegrationBuilderNodes = any;
const createLinesForIntegrationBuilder = (
  nodes: IntegrationBuilderNodes,
): Array<string> => {
  if (nodes.map === undefined) {
    return [];
  }
  const chunk = nodes
    .map((node) => {
      if (node.name === 'KeyToken' && node !== 'string' && node.attributes) {
        return node.attributes.value || '';
      } else if (typeof node === 'string') {
        return node;
      } else {
        console.warn(`[Warning] Parsing a type of tag that is unsupported`); // eslint-disable-line no-console
        return '';
      }
    })
    .join('');
  let lines = [];
  if (chunk === '\n') {
    // Keep any plain newlines seperating chunks of code
    lines = [chunk];
  } else {
    // Within a Chunk with multiple lines we need to split by newline while keeping any
    // newlines that act as content / spacing in between lines. Once negative lookbehinds
    // are supported we can clean this up with a regex but for now :(
    lines = chunk.split(/(\n)/g).filter(Boolean);
    lines = lines.filter((l, i) => {
      if (l === '\n' && i < lines.length - 1) {
        // Only filter out single newlines that signify the end of a line of code
        // while keeping \n\n that spaces out content
        return lines[i - 1] === '\n';
      }
      return true;
    });
  }

  return lines;
};

const formatForIntegrationBuilder = (
  node: MarkdocAstNode,
  config: MarkdocConfig,
) => {
  const attributes = node.renderAttributes(node.attributes, config);
  const children = node.renderChildren(config);
  const fileSteps = []; // Used to keep track of which File contains which Step

  const formatNode = (n, i, steps = []) => {
    if (typeof n === 'string') {
      const chunks = createLinesForIntegrationBuilder([n]);
      if (i === children.length - 1 && chunks[chunks.length - 1] === '\n') {
        // CodeBlocks end with a \n, but we do not want to
        // parse and render this in the IB
        chunks.pop();
      }
      return [
        new Tag(
          'Chunk',
          {
            language: attributes.language,
            chunks,
            steps,
            key: `${String(attributes.language)}-${i}`,
          },
          [],
        ),
      ];
    } else if (isMarkdocAstTag(n) && n.name === 'Step') {
      let chunks;
      if (findMarkdocTag(n.children, (c) => c.name === 'Step')) {
        // Steps can be nested within Files
        // We need to recursively create the Chunks for those nested Steps
        // while still keeping them inline with surrounding lines
        chunks = n.children
          .map((c, j) => {
            if (typeof c === 'string') {
              return createLinesForIntegrationBuilder([c]);
            } else {
              return formatNode(c, j, [...steps, n.attributes.id]);
            }
          })
          .flat();
      } else {
        chunks = createLinesForIntegrationBuilder(n.children);
      }
      const attrs = {
        id: n.attributes.id,
        key: `${String(n.attributes.id)}-${String(attributes.language)}-${i}`,
        language: attributes.language,
        steps: [...steps, n.attributes.id],
        when: n.attributes.when,
        unless: n.attributes.unless,
        chunks,
      };

      fileSteps.push(n.attributes.id);
      return [new Tag('Chunk', attrs, n.children)];
    } else {
      return [];
    }
  };

  const chunks = flattenDeep(
    children.map((c, i) => formatNode(c, i, [])).filter((c) => c.length),
  );

  return new Tag(
    'File',
    {
      language: attributes.language,
      filename: attributes.filename,
      downloadOnly: attributes.downloadOnly,
      when: attributes.when,
      steps: fileSteps,
    },
    chunks,
  );
};

// Counts the number of lines in a string that is a child of a rendered CodeBlock node
const lineCount = (content: string) => {
  let count = content.split('\n').length;

  if (content.length === 0) {
    return 0;
  }

  if (/^\n+$/.test(content)) {
    // If the content is only new lines, doing a 'split' is not going to work since
    // '\n\n\n'.split('\n').length -> 4 when we want to return 3
    count = content.length;
  } else if (content[0] === '\n') {
    // If there are multiple children in the CodeBlock, then any child text nodes
    // will start with a newline. This new line should not be apart of our count,
    // since the fact that this function is being called with new text content
    // means we are implicitly counting for the fact that we have a new line.

    // Consider the following example to understand why:
    //   currentLine = 1
    //   currentLine += lineCount("Line 1\nLine 2")
    //   /* currentLine should be 3 */
    //   currentLine += lineCount("\nLine 3")
    //   /* currentLine should be 4 */
    //   currentLine += lineCount("\nLine 4\n")
    //   /* currentLine should be 5 */
    count--;
  }

  return count;
};

const cleanChildren = (children) =>
  children
    .map((child) => {
      if (
        (isMarkdocAstTag(child) && child.name !== 'highlight') ||
        (!isMarkdocAstTag(child) && typeof child !== 'string')
      ) {
        return 'TAG';
      } else {
        return child;
      }
    })
    .reduce((result, curr) => {
      if (result) {
        // This function combines strings with tags into the same line to
        // make calculating highlight ranges easier. For example, the line
        // `stripe = loadStripe('{% key %}')`
        // wouild get parsed into [`stripe = loadStripe('`, Tag, `')`]
        // since these are all the same line we replace Tag with "TAG" and
        // coalesce the strings so we avoid having to account for numerous edge
        // cases when calculating highlight ranges. (This function is written in a weird
        // way to appease flow.)
        const prev = result[result.length - 1];
        if (typeof prev === 'string' && typeof curr === 'string') {
          result[result.length - 1] = prev + curr;
        } else {
          result.push(curr);
        }
        return result;
      } else {
        return result;
      }
    }, []);

const getHighlights = (
  children: Array<MarkdocRenderNode | string>,
): CodeBlockHighlights => {
  // Formats highlighted code ranges
  // Each highlighted range consists of {start, end}
  // A CodeBlock can have several ranges of different types (e.g. default vs diffs)
  const highlights: CodeBlockHighlights = {
    default: [],
    add: [],
    remove: [],
  };

  // This Markdoc text:
  //  ```
  //  LINE 1
  //  LINE 2
  //  {% highlight %}
  //  [highlight] LINE 3
  //  {% /highlight %}
  //  LINE 4
  //  ```
  // Will be parsed and turned into a code fence node, with 3 children.
  // Child 1: Text Node, contents: "LINE 1\nLINE 2"
  // Child 2: Highlight Node, child with contents: "\n[highlight] LINE 3"
  // Child 3: Text Node, contents: "\nLINE 4\n"

  // It is important to note that Markdoc parses the text inside code fences differently
  // than other tags, which is why there are new lines in the contents. When we call renderChildren()
  // on these nodes, we get back a string, a highligh tag, and a string.

  // The highlights index starts at 1, not 0
  let line = 1;
  cleanChildren(children).forEach((child, index) => {
    if (typeof child === 'string') {
      line += lineCount(child);
      // In an edge case where the first node in the code fence is a text node that
      // contains a literal new line as the first character, we need to count it.
      if (child[0] === '\n' && index === 0) {
        line++;
      }
    } else if (isMarkdocAstTag(child) && child.name === 'highlight') {
      const highlight = {};
      highlight.start = line;
      const highlightedContent = cleanChildren(child.children)
        .map((innerChild) => {
          if (typeof innerChild === 'string') {
            return innerChild;
          } else {
            // To simplify, we treat any non string children (like tags or variables) as empty strings
            return '';
          }
        })
        .join('');
      line += lineCount(highlightedContent);
      highlight.end = line - 1;
      const type =
        typeof child.attributes.type === 'string'
          ? child.attributes.type
          : 'default';
      if (highlights[type]) {
        highlights[type].push(highlight);
      }
    }
  });

  return highlights;
};

const flattenHighlightTags = (children) => {
  // We need to get rid of any highlight tags
  return children.flatMap((child) => {
    if (isMarkdocAstTag(child) && child.name === 'highlight') {
      return child.children;
    } else {
      return [child];
    }
  });
};

export const CodeBlock: MarkdocNodeRegistration = {
  node: 'fence',
  component: 'CodeBlock',
  localize: false,
  attributes: {
    language: {
      type: String,
      description:
        'The programming language of the code block. Place it after the backticks.',
    },
    filename: {
      type: String,
      description: 'This filename will be displayed in the header.',
    },
    link: {
      type: String,
      description:
        'Pass in a GitHub link to show a button on the header to link to the sample.',
    },
    icon: {
      type: String,
      description:
        'Pass in an Sail icon to override the default icon that is shown in the header. (View go/sail/Icon).',
    },
    numbered: {
      type: Boolean,
      default: true,
      description: 'Set to false to disable line numbers.',
    },
    maxLines: {
      type: Number,
      description:
        'Set a maximum number of lines to display. The rest will be visible by clicking a toggle.',
    },
    'integration-builder': {type: Boolean, render: false, default: false},
    when: {
      type: Object,
      description:
        'Used only for the integration builder -- specifies the condition in which the block is displayed',
    },
    downloadOnly: {
      type: Boolean,
      default: false,
      description:
        'Used only for the integration builder -- set to true to not show this code sample in the UI (e.g. a README or build file).',
    },
    content: {type: String, render: false},
    process: {type: Boolean, render: false, default: true},
    'allow-secret-keys': {
      type: Boolean,
      render: false,
      default: false,
      description: 'Must set to true to enable showing secret keys.',
    },
  },
  validate(node) {
    if (node.attributes['allow-secret-keys'] === true) {
      return [];
    }

    const errors = [];
    for (const child of node.walk()) {
      if (child.type === 'tag:key' && child.attributes.type === 'secret') {
        errors.push({
          id: 'fence-forbidden-secret-key',
          level: 'critical',
          message:
            'Fenced code block must set the attribute `allow-secret-keys` in order to display a secret key.',
        });
      }
    }

    return errors;
  },
  render(node, config) {
    if (node.attributes['integration-builder'] === true) {
      return formatForIntegrationBuilder(node, config);
    }
    const newConfig = {
      ...config,
      variables: {
        ...config.variables,
        '$$CodeBlock:language': node.attributes.language,
      },
    };

    const content: string = (node.attributes.content: any);

    const children = node.children.length
      ? node.renderChildren(newConfig)
      : [content];

    // We perform the highlight range calculation on the render tree (aka the output on node.renderChildren)
    // instead of node.children to avoid having to deal with conditionals, resolve Markdoc fragments, etc.
    const highlights = getHighlights(children);

    const attrs = {
      ...node.renderAttributes(this.attributes, config),
      highlights,
    };

    return new Tag(this.tag, attrs, flattenHighlightTags(children));
  },
};
